import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Button, Select } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EventIcon from "@material-ui/icons/Event";
import { format } from "date-fns";

import MainContainer from "../../components/MainContainer";
import ReportsNav from "../../components/ReportsNav";
import useAerosSettingsStyles from "../../components/Settings/aerosSettingsStyles";
import { AEROS } from "../../styles/aerosTokens";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

// Tela "Motivos de finalização" (rota /relatorio-vendas), no padrão Aeros:
// header de 60px, filtros em pills, KPIs, chips de motivos e tabelas em CSS
// grid. A lógica (GET /users, GET /ticketreport/vendas, filtros client-side)
// é a mesma de antes — só o layout mudou.
const t = (k, o) => i18n.t(`aeros.sales.${k}`, o);
const TICKETS_GRID = "70px 1.6fr 1.1fr 1fr 1.4fr 1.4fr";
const ATENDENTES_GRID = "2fr 1fr 1fr 1.4fr";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const pillBorder = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  return {
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "0 12px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"
      }`,
    },
    pageTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    pageTitleCount: {
      color: theme.palette.text.secondary,
      fontWeight: 500,
    },
    pageSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
    },
    pageBody: {
      flex: 1,
      padding: "16px 12px",
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    filterBar: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      flexWrap: "wrap",
      marginBottom: 14,
    },
    pill: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${pillBorder}`,
      borderRadius: 10,
      padding: "8px 12px",
      fontSize: 12.5,
      color: theme.palette.text.secondary,
    },
    pillIcon: {
      fontSize: 16,
      color: theme.palette.text.secondary,
    },
    pillSelect: {
      fontSize: 12.5,
      minWidth: 120,
      color: theme.palette.text.primary,
      "& .MuiNativeSelect-select": {
        padding: 0,
        paddingRight: 20,
        background: "transparent",
        "&:focus": { background: "transparent" },
      },
      "& .MuiNativeSelect-icon": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        top: "calc(50% - 9px)",
      },
    },
    dateField: {
      background: "transparent",
      border: "none",
      outline: "none",
      padding: 0,
      fontFamily: "inherit",
      fontSize: 12.5,
      color: theme.palette.text.primary,
      colorScheme: theme.palette.type,
    },
    dateSep: {
      color: theme.palette.text.secondary,
    },
    kpiGrid: {
      marginBottom: 16,
    },
    // Com dados de venda são 4 KPIs — grid3 deixaria o quarto sozinho na linha.
    kpiGrid4: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 10,
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    section: {
      marginBottom: 16,
    },
    chipsRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
    },
    ticketsHead: { gridTemplateColumns: TICKETS_GRID },
    ticketsRow: { gridTemplateColumns: TICKETS_GRID },
    atendentesHead: { gridTemplateColumns: ATENDENTES_GRID },
    atendentesRow: { gridTemplateColumns: ATENDENTES_GRID },
    ellipsis: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    numberCell: {
      fontWeight: 600,
    },
    truncatedNote: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginBottom: 8,
    },
    emptyState: {
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
      padding: "28px 0",
      textAlign: "center",
    },
  };
});

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number(value) || 0);

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return format(date, "dd/MM/yyyy HH:mm");
};

const RelatorioVendas = () => {
  const classes = useStyles();
  const aeros = useAerosSettingsStyles();
  const [loading, setLoading] = useState(false);
  const [relatorio, setRelatorio] = useState(null);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({
    dateFrom: format(new Date().setDate(1), "yyyy-MM-dd"),
    dateTo: format(new Date(), "yyyy-MM-dd"),
    userId: "",
    motivoNaoVenda: "",
    motivoFinalizacao: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data.users);
    } catch (err) {
      toastError(err);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleGenerateReport = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      toast.error(t("selectPeriod"));
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      });

      if (filters.userId) {
        params.append("userId", filters.userId);
      }

      const { data } = await api.get(`/ticketreport/vendas?${params}`);
      setRelatorio(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const motivosFinalizacao = relatorio?.motivosFinalizacao || [];
  const motivosNaoVenda = relatorio?.motivosNaoVenda || [];
  const atendentes = relatorio?.atendentes || [];
  const tickets = relatorio?.tickets || [];

  // Filtros client-side por motivo (aplicados depois de gerar o relatório).
  const filteredTickets = tickets.filter((ticket) => {
    let motivoNaoVendaOk = true;
    let motivoFinalizacaoOk = true;
    if (filters.motivoNaoVenda) {
      motivoNaoVendaOk = ticket.motivoNaoVenda === filters.motivoNaoVenda;
    }
    if (filters.motivoFinalizacao) {
      motivoFinalizacaoOk =
        ticket.motivoFinalizacao === filters.motivoFinalizacao;
    }
    return motivoNaoVendaOk && motivoFinalizacaoOk;
  });

  // "Finalizados" = todos os tickets encerrados no período. `totalTickets` vem
  // do backend e ignora o teto de 2.000 da lista; sem ele, cai no tamanho da
  // lista e, por fim, na soma dos totais.
  const totalFinalizados = relatorio
    ? relatorio.totalTickets ||
      tickets.length ||
      (relatorio.totalVendas || 0) + (relatorio.totalNaoVendas || 0)
    : 0;
  const totalComMotivo = motivosFinalizacao.reduce(
    (sum, item) => sum + (item.quantidade || 0),
    0
  );
  const totalVendas = relatorio?.totalVendas || 0;
  const totalValorVendas = relatorio?.totalValorVendas || 0;
  const temVenda = totalVendas > 0 || totalValorVendas > 0;

  return (
    <MainContainer>
      <div className={classes.pageHeader}>
        <div>
          <div className={classes.pageTitle}>
            {t("title")}
            {relatorio && (
              <span className={classes.pageTitleCount}> · {totalFinalizados}</span>
            )}
          </div>
          <div className={classes.pageSubtitle}>
            {t("subtitle")}
          </div>
        </div>
      </div>

      <div className={classes.pageBody}>
        <ReportsNav />

        <div className={classes.filterBar}>
          <div className={classes.pill}>
            <EventIcon className={classes.pillIcon} />
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
              className={classes.dateField}
              aria-label={i18n.t("aeros.reports.dateFrom")}
            />
            <span className={classes.dateSep}>–</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange("dateTo", e.target.value)}
              className={classes.dateField}
              aria-label={i18n.t("aeros.reports.dateTo")}
            />
          </div>
          <div className={classes.pill}>
            <span>{t("agent")}</span>
            <Select
              native
              disableUnderline
              value={filters.userId}
              onChange={(e) => handleFilterChange("userId", e.target.value)}
              className={classes.pillSelect}
              inputProps={{ "aria-label": "Atendente" }}
            >
              <option value="">{t("all")}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={aeros.gradientButton}
            onClick={handleGenerateReport}
            disabled={loading}
          >
            {loading ? t("generating") : t("generate")}
          </Button>
        </div>

        {!relatorio ? (
          <div className={aeros.tableCard}>
            <div className={classes.emptyState}>
              {t("emptyHint")}
            </div>
          </div>
        ) : (
          <>
            <div
              className={`${temVenda ? classes.kpiGrid4 : aeros.grid3} ${
                classes.kpiGrid
              }`}
            >
              <div className={aeros.cardTight}>
                <div className={aeros.kpiValue}>{totalFinalizados}</div>
                <div className={aeros.kpiLabel}>{t("finished")}</div>
              </div>
              <div className={aeros.cardTight}>
                <div className={aeros.kpiValue}>{totalComMotivo}</div>
                <div className={aeros.kpiLabel}>{t("withReason")}</div>
              </div>
              {temVenda && (
                <div className={aeros.cardTight}>
                  <div className={`${aeros.kpiValue} ${aeros.textSuccess}`}>
                    {totalVendas}
                  </div>
                  <div className={aeros.kpiLabel}>{t("sales")}</div>
                </div>
              )}
              {temVenda && (
                <div className={aeros.cardTight}>
                  <div className={`${aeros.kpiValue} ${aeros.textSuccess}`}>
                    {formatCurrency(totalValorVendas)}
                  </div>
                  <div className={aeros.kpiLabel}>{t("totalValue")}</div>
                </div>
              )}
            </div>

            {motivosFinalizacao.length > 0 && (
              <div className={classes.section}>
                <div className={aeros.microLabel}>{t("reasons")}</div>
                <div className={classes.chipsRow}>
                  {motivosFinalizacao.map((item) => (
                    <span key={item.motivo} className={aeros.chipPurple}>
                      {item.motivo} ({item.quantidade})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {motivosNaoVenda.length > 0 && (
              <div className={classes.section}>
                <div className={aeros.microLabel}>{t("noSaleReasons")}</div>
                <div className={classes.chipsRow}>
                  {motivosNaoVenda.map((item) => (
                    <span key={item.motivo} className={aeros.chipNeutral}>
                      {item.motivo} ({item.quantidade})
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className={classes.filterBar}>
              <div className={classes.pill}>
                <span>{t("finishReason")}:</span>
                <Select
                  native
                  disableUnderline
                  value={filters.motivoFinalizacao}
                  onChange={(e) =>
                    handleFilterChange("motivoFinalizacao", e.target.value)
                  }
                  className={classes.pillSelect}
                  inputProps={{ "aria-label": t("finishReason") }}
                >
                  <option value="">{t("all")}</option>
                  {motivosFinalizacao.map((item) => (
                    <option key={item.motivo} value={item.motivo}>
                      {item.motivo}
                    </option>
                  ))}
                </Select>
              </div>
              <div className={classes.pill}>
                <span>{t("noSaleReason")}:</span>
                <Select
                  native
                  disableUnderline
                  value={filters.motivoNaoVenda}
                  onChange={(e) =>
                    handleFilterChange("motivoNaoVenda", e.target.value)
                  }
                  className={classes.pillSelect}
                  inputProps={{ "aria-label": t("noSaleReason") }}
                >
                  <option value="">{t("all")}</option>
                  {motivosNaoVenda.map((item) => (
                    <option key={item.motivo} value={item.motivo}>
                      {item.motivo}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {relatorio.ticketsTruncated && (
              <div className={classes.truncatedNote}>
                Mostrando os 2.000 tickets mais recentes de{" "}
                {relatorio.totalTickets}. Refine o período para ver todos.
              </div>
            )}

            <div className={`${aeros.tableCard} ${classes.section}`}>
              <div className={`${aeros.tableHead} ${classes.ticketsHead}`}>
                <div className={`${aeros.headCell} ${aeros.headCellFirst}`}>
                  ID
                </div>
                <div className={aeros.headCell}>{i18n.t("aeros.reports.filters.user")}</div>
                <div className={aeros.headCell}>{t("date")}</div>
                <div className={aeros.headCell}>{t("value")}</div>
                <div className={aeros.headCell}>{t("noSaleReason")}</div>
                <div className={aeros.headCell}>{t("finishReason")}</div>
              </div>
              {filteredTickets.length === 0 ? (
                <div className={aeros.emptyRow}>
                  Nenhum ticket finalizado no período
                </div>
              ) : (
                filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`${aeros.tableRow} ${classes.ticketsRow}`}
                  >
                    <div className={`${aeros.cell} ${aeros.cellFirst}`}>
                      {ticket.id}
                    </div>
                    <div
                      className={`${aeros.cell} ${classes.ellipsis}`}
                      title={ticket.user?.name || ""}
                    >
                      {ticket.user?.name || "—"}
                    </div>
                    <div className={aeros.cell}>
                      {formatDateTime(ticket.updatedAt)}
                    </div>
                    <div className={`${aeros.cell} ${classes.numberCell}`}>
                      {ticket.valorVenda
                        ? formatCurrency(ticket.valorVenda)
                        : "—"}
                    </div>
                    <div
                      className={`${aeros.cell} ${classes.ellipsis}`}
                      title={ticket.motivoNaoVenda || ""}
                    >
                      {ticket.motivoNaoVenda || "—"}
                    </div>
                    <div className={aeros.cell}>
                      {ticket.motivoFinalizacao ? (
                        <span className={aeros.chipPurple}>
                          {ticket.motivoFinalizacao}
                        </span>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className={aeros.microLabel}>{t("byAgent")}</div>
            <div className={aeros.tableCard}>
              <div className={`${aeros.tableHead} ${classes.atendentesHead}`}>
                <div className={`${aeros.headCell} ${aeros.headCellFirst}`}>
                  Atendente
                </div>
                <div className={aeros.headCell}>{t("finished")}</div>
                <div className={aeros.headCell}>{t("sales")}</div>
                <div className={aeros.headCell}>{t("value")}</div>
              </div>
              {atendentes.length === 0 ? (
                <div className={aeros.emptyRow}>
                  Nenhum ticket finalizado no período
                </div>
              ) : (
                atendentes.map((atendente) => (
                  <div
                    key={atendente.id}
                    className={`${aeros.tableRow} ${classes.atendentesRow}`}
                  >
                    <div
                      className={`${aeros.cell} ${aeros.cellFirst} ${classes.ellipsis}`}
                      title={atendente.name}
                    >
                      {atendente.name}
                    </div>
                    <div className={aeros.cell}>
                      {atendente.totalTickets || 0}
                    </div>
                    <div className={aeros.cell}>
                      {atendente.totalVendas || 0}
                    </div>
                    <div className={`${aeros.cell} ${classes.numberCell}`}>
                      {formatCurrency(atendente.totalValorVendas)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default RelatorioVendas;
